import { BrowserRouter } from "react-router-dom";
import { Layout } from "./components/layout";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import FormPage from "./pages/form-page/form-page";
import {
    AuthConfig,
    AuthGuard,
    ClientType,
    IDPType,
    useDynaTraceAnalyticsHandler,
} from "@fce-bees/fce-common-components";
import { useEffect, useState } from "react";
import i18n from "./i18n";
import { v4 as uuidv4 } from "uuid";
import { ResultsPage } from "src/pages/results-page/results-page";
import { UclThemeProvider } from "src/components/ucl-theme-provider";
import { MaintenancePage } from "src/pages/maintenance-page/maintenance-page";
import { EligibilityResultType } from "src/types/general";
import { useCookiesPoll } from "src/hooks/use-cookie-poll";
import { useAdobeAnalytics } from "src/hooks/use-adobe-analytics";
import { logAuthGuardFailure } from "src/lib/logger";
import { Flags } from "src/consts/flags";
import { useBooleanFlag } from "src/consts/flagWrapper";

const authConfig: AuthConfig = {
    idpClientId: import.meta.env.VITE_TENANT_ID,
    baseAuthorizationURL: "",
    redirectURI: `${import.meta.env.VITE_IMS_BASE_URL}${import.meta.env.VITE_IMS_AAD_EXCHANGE_URL}`,
    responseMode: "form_post",
};

export default function App() {
    const correlationId = uuidv4();
    const [eligibilityResult, setEligibilityResult] = useState<EligibilityResultType | null>(null);
    const [formFocused, setFormFocused] = useState<boolean>(false);
    const cookiePreferences = useCookiesPoll(formFocused);
    const maintenanceDecision = useBooleanFlag(Flags.MAINTENANCE_WINDOW);
    const { trackInitialPageLoad, trackEligibilityResult } = useAdobeAnalytics(cookiePreferences);
    const [showMaintenance, setShowMaintenance] = useState<boolean>(false);
    const basename = window?.location?.pathname ?? "/";
    const cookieOverride = import.meta.env.VITE_COOKIE_OVERRIDE === "true";

    useDynaTraceAnalyticsHandler({
        src: import.meta.env.VITE_PREQUAL_DYNATRACE_SCRIPT_SRC,
        trackCookies: cookieOverride || cookiePreferences,
    });

    useEffect(() => {
        if (maintenanceDecision) {
            setShowMaintenance(maintenanceDecision);
        }
    }, [maintenanceDecision]);

    useEffect(() => {
        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target;

            if (target instanceof HTMLInputElement && target.form) {
                setFormFocused(true);
                document.removeEventListener("focusin", handleFocusIn);
            }
        };

        document.addEventListener("focusin", handleFocusIn);
        return () => document.removeEventListener("focusin", handleFocusIn);
    }, []);

    useEffect(() => {
        const analyticsTracking = () => {
            if (!cookiePreferences) return;

            if (eligibilityResult) {
                trackEligibilityResult(eligibilityResult.result);
            } else {
                trackInitialPageLoad();
            }
        };

        analyticsTracking();
    }, [cookiePreferences, eligibilityResult, trackInitialPageLoad, trackEligibilityResult]);

    const applicationId = import.meta.env.VITE_AUTH_APP_ID;
    const imsBaseUrl = import.meta.env.VITE_IMS_BASE_URL;

    const handleAuthError = () => {
        logAuthGuardFailure(correlationId);
        setShowMaintenance(true);
    };

    const handleFormSuccess = (result: EligibilityResultType) => {
        setEligibilityResult(result);
        window.scrollTo(0, 0);
    };

    return (
        <I18nextProvider i18n={i18n}>
            <HelmetProvider>
                <UclThemeProvider>
                    <BrowserRouter basename={basename}>
                        <Layout>
                            {showMaintenance ? (
                                <MaintenancePage />
                            ) : (
                                <AuthGuard
                                    {...{ applicationId, imsBaseUrl }}
                                    authType={"COOKIE"}
                                    cookieDomain={".ford.com"}
                                    onError={handleAuthError}
                                    idpType={IDPType.guest}
                                    clientType={"guest" as ClientType}
                                    authConfig={authConfig}
                                    market="en-GB"
                                    applicationBasePath={`${window.location.origin}`}
                                >
                                    {!eligibilityResult && (
                                        <FormPage
                                            correlationId={correlationId}
                                            cookiePreferences={cookiePreferences}
                                            onSubmitSuccess={handleFormSuccess}
                                        />
                                    )}
                                    {eligibilityResult && (
                                        <ResultsPage
                                            result={eligibilityResult.result}
                                            customerName={eligibilityResult.customerName}
                                        />
                                    )}
                                </AuthGuard>
                            )}
                        </Layout>
                    </BrowserRouter>
                </UclThemeProvider>
            </HelmetProvider>
        </I18nextProvider>
    );
}
